// Hook Editor — dashboard authoring shell for a product's attachment hooks
// (ATTACHMENT-SYSTEM-DESIGN.md). Loads the product's hook rows + the global
// connector registry, lets the author add/edit/remove hooks numerically in an
// inspector, visualizes them in a placeholder three.js viewport, and PUTs the
// full replacement set on Save. The true 3D placement affordance is the
// planned Unity editor — see UnityEditorSeam; this shell is everything else:
// navigation, data, numeric editing, compatibility lookup.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchConnectorProducts,
  fetchConnectors,
  fetchProductHooks,
  saveProductHooks,
} from "./api/attachmentClient";
import type { CompatibleProductDto, HookConnectorDto, HookKind, ProductHookDto } from "./types";
import { HookViewport } from "./viewport/HookViewport";

export interface HookEditorToolProps {
  /** Base URL of the deployment. Empty = same origin. */
  baseUrl: string;
  /** The product whose hooks are being authored. */
  productId: string;
  /** Display-only heading; the editor works without it. */
  productName?: string;
}

function newId(): string {
  const c = globalThis.crypto as (Crypto & { randomUUID?: () => string }) | undefined;
  if (c?.randomUUID) return c.randomUUID();
  // Fallback GUID v4 for older embed contexts without crypto.randomUUID.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, ch => {
    const r = (Math.random() * 16) | 0;
    const v = ch === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Trim float noise for display without losing authoring precision. */
function fmt(v: number): string {
  return String(Math.round(v * 1e6) / 1e6);
}

/** Facing = local +Z rotated by the hook quaternion — the direction the hook
 *  must point for a mate (hooks mate facing each other). Expanded q·v·q⁻¹ for
 *  v = (0,0,1) so the inspector doesn't need a three.js import. */
function facingVector(x: number, y: number, z: number, w: number): [number, number, number] {
  const len = Math.hypot(x, y, z, w) || 1;
  const qx = x / len, qy = y / len, qz = z / len, qw = w / len;
  return [
    2 * (qx * qz + qw * qy),
    2 * (qy * qz - qw * qx),
    1 - 2 * (qx * qx + qy * qy),
  ];
}

function dominantAxis(v: [number, number, number]): string {
  const abs = v.map(Math.abs);
  const i = abs.indexOf(Math.max(...abs));
  const axis = ["X", "Y", "Z"][i];
  return `${v[i] >= 0 ? "+" : "−"}${axis}`;
}

export function HookEditorTool(props: HookEditorToolProps) {
  const [connectors, setConnectors] = useState<HookConnectorDto[]>([]);
  const [hooks, setHooks] = useState<ProductHookDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ── Initial load: connector registry + the product's hooks ──────────
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    Promise.all([
      fetchConnectors({ baseUrl: props.baseUrl, signal: ctrl.signal }),
      fetchProductHooks({ baseUrl: props.baseUrl, productId: props.productId, signal: ctrl.signal }),
    ])
      .then(([conns, hks]) => {
        setConnectors(conns);
        setHooks(hks);
        setSelectedId(hks.length > 0 ? hks[0].id : null);
        setDirty(false);
      })
      .catch(err => {
        if ((err as Error).name === "AbortError") return;
        setError((err as Error).message ?? String(err));
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [props.baseUrl, props.productId]);

  const selected = useMemo(
    () => hooks.find(h => h.id === selectedId) ?? null,
    [hooks, selectedId],
  );
  const connectorById = useMemo(() => {
    const map = new Map<string, HookConnectorDto>();
    for (const c of connectors) map.set(c.id, c);
    return map;
  }, [connectors]);

  const mutateHook = useCallback((id: string, patch: Partial<ProductHookDto>) => {
    setHooks(prev => prev.map(h => (h.id === id ? { ...h, ...patch } : h)));
    setDirty(true);
    setSaveError(null);
  }, []);

  function addHook(kind: HookKind) {
    const hook: ProductHookDto = {
      id: newId(),
      productId: props.productId,
      kind,
      name: `Hook ${hooks.length + 1}`,
      posX: 0, posY: 0.5, posZ: 0.5,
      rotX: 0, rotY: 0, rotZ: 0, rotW: 1,
      connectorId: connectors[0]?.id ?? "",
      context: 0,
      required: false,
      swivel: false,
      connectionGroup: 0,
      ...(kind === "HookLine" ? { width: 1.0, step: 0, mustBeFullyInsideTarget: false } : {}),
      slotPickable: false,
      sortOrder: hooks.reduce((max, h) => Math.max(max, h.sortOrder), -1) + 1,
    };
    setHooks(prev => [...prev, hook]);
    setSelectedId(hook.id);
    setDirty(true);
  }

  function removeHook(id: string) {
    setHooks(prev => prev.filter(h => h.id !== id));
    setSelectedId(prev => (prev === id ? null : prev));
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    setSaveError(null);
    try {
      await saveProductHooks({ baseUrl: props.baseUrl, productId: props.productId, hooks });
      setDirty(false);
    } catch (err) {
      setSaveError((err as Error).message ?? String(err));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="dptp-state dptp-state--loading">Loading hooks…</div>;
  if (error) return <div className="dptp-state dptp-state--error">Error: {error}</div>;

  return (
    <div className="hke">
      <header className="hke__topbar">
        <div className="hke__title">
          <h2>Hook editor</h2>
          <span className="hke__subtitle">{props.productName ?? props.productId}</span>
        </div>
        <div className="hke__topbar-actions">
          {dirty && <span className="hke__dirty" title="Unsaved changes">● Unsaved changes</span>}
          {saveError && <span className="hke__save-error" title={saveError}>Save failed</span>}
          <button
            type="button"
            className="hke__save-btn"
            disabled={!dirty || saving}
            onClick={() => void save()}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </header>

      <div className="hke__body">
        {/* ── Left: hook list ─────────────────────────────────────────── */}
        <aside className="hke__list-panel">
          <div className="hke__panel-head">
            <span>Hooks ({hooks.length})</span>
            <div className="hke__add-btns">
              <button type="button" className="hke__add-btn" onClick={() => addHook("HookPoint")}>
                + Point
              </button>
              <button type="button" className="hke__add-btn" onClick={() => addHook("HookLine")}>
                + Line
              </button>
            </div>
          </div>
          {hooks.length === 0 ? (
            <p className="hke__muted">No hooks yet. Add a point or line hook to make this product attachable.</p>
          ) : (
            <ul className="hke__list">
              {[...hooks]
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map(hook => {
                  const conn = connectorById.get(hook.connectorId);
                  const contextLabel =
                    conn?.type === "MaleFemale"
                      ? (hook.context === 0 ? (conn.maleName ?? "Male") : (conn.femaleName ?? "Female"))
                      : null;
                  return (
                    <li key={hook.id}>
                      <button
                        type="button"
                        className={"hke__list-row" + (hook.id === selectedId ? " hke__list-row--selected" : "")}
                        onClick={() => setSelectedId(hook.id)}
                      >
                        <span className="hke__list-name">{hook.name || "(unnamed)"}</span>
                        <span className={"hke__kind-badge" + (hook.kind === "HookLine" ? " hke__kind-badge--line" : "")}>
                          {hook.kind === "HookPoint" ? "Point" : "Line"}
                        </span>
                        <span className="hke__list-meta">
                          {conn ? conn.connectorKey : <em className="hke__invalid">no connector</em>}
                          {contextLabel ? ` · ${contextLabel}` : ""}
                        </span>
                      </button>
                    </li>
                  );
                })}
            </ul>
          )}
        </aside>

        {/* ── Center: placeholder viewport (Unity editor seam) ─────────── */}
        <main className="hke__viewport-panel">
          <HookViewport hooks={hooks} selectedId={selectedId} onSelect={setSelectedId} />
        </main>

        {/* ── Right: inspector + compatibility ─────────────────────────── */}
        <aside className="hke__inspector-panel">
          {selected ? (
            <HookInspector
              key={selected.id}
              hook={selected}
              connectors={connectors}
              baseUrl={props.baseUrl}
              productId={props.productId}
              onChange={patch => mutateHook(selected.id, patch)}
              onRemove={() => removeHook(selected.id)}
            />
          ) : (
            <p className="hke__muted">Select a hook in the list or viewport to edit it.</p>
          )}
        </aside>
      </div>
    </div>
  );
}

// ─── Inspector ────────────────────────────────────────────────────────────

function HookInspector(props: {
  hook: ProductHookDto;
  connectors: HookConnectorDto[];
  baseUrl: string;
  productId: string;
  onChange: (patch: Partial<ProductHookDto>) => void;
  onRemove: () => void;
}) {
  const { hook, connectors, onChange } = props;
  const connector = connectors.find(c => c.id === hook.connectorId) ?? null;
  const facing = facingVector(hook.rotX, hook.rotY, hook.rotZ, hook.rotW);
  const quatLen = Math.hypot(hook.rotX, hook.rotY, hook.rotZ, hook.rotW);
  const quatOff = Math.abs(quatLen - 1) > 1e-4;

  return (
    <div className="hke__inspector">
      <div className="hke__field">
        <label className="hke__label">Name</label>
        <input
          type="text"
          className="hke__input"
          value={hook.name ?? ""}
          placeholder="Hook name (debug label)"
          onChange={e => onChange({ name: e.target.value || undefined })}
        />
      </div>

      <fieldset className="hke__group">
        <legend>Position (local)</legend>
        <div className="hke__triple">
          <NumberField label="X" value={hook.posX} onChange={v => onChange({ posX: v })} />
          <NumberField label="Y" value={hook.posY} onChange={v => onChange({ posY: v })} />
          <NumberField label="Z" value={hook.posZ} onChange={v => onChange({ posZ: v })} />
        </div>
      </fieldset>

      <fieldset className="hke__group">
        <legend>Rotation (quaternion)</legend>
        <div className="hke__quad">
          <NumberField label="X" value={hook.rotX} onChange={v => onChange({ rotX: v })} />
          <NumberField label="Y" value={hook.rotY} onChange={v => onChange({ rotY: v })} />
          <NumberField label="Z" value={hook.rotZ} onChange={v => onChange({ rotZ: v })} />
          <NumberField label="W" value={hook.rotW} onChange={v => onChange({ rotW: v })} />
        </div>
        <p className="hke__note">
          Local rotation as [x, y, z, w]. Facing is local +Z rotated by it — two hooks must
          face into each other to mate.
        </p>
        <div className="hke__facing">
          <span className="hke__facing-arrow" aria-hidden="true">➤</span>
          Facing ≈ ({fmt(facing[0])}, {fmt(facing[1])}, {fmt(facing[2])}) — mostly {dominantAxis(facing)}
        </div>
        {quatOff && (
          <button
            type="button"
            className="hke__mini-btn"
            onClick={() => {
              const len = quatLen || 1;
              onChange({
                rotX: hook.rotX / len,
                rotY: hook.rotY / len,
                rotZ: hook.rotZ / len,
                rotW: hook.rotW / len,
              });
            }}
          >
            Normalize (|q| = {fmt(quatLen)})
          </button>
        )}
      </fieldset>

      <fieldset className="hke__group">
        <legend>Connector</legend>
        <select
          className="hke__input"
          value={hook.connectorId}
          onChange={e => onChange({ connectorId: e.target.value })}
        >
          {hook.connectorId === "" && <option value="">— pick a connector —</option>}
          {connectors.map(c => (
            <option key={c.id} value={c.id}>
              {c.connectorKey} ({c.type})
            </option>
          ))}
        </select>
        {!connector && <p className="hke__invalid">A hook without a connector is invalid.</p>}
        <div className="hke__field">
          <label className="hke__label">Context</label>
          <select
            className="hke__input"
            value={hook.context}
            disabled={connector?.type !== "MaleFemale"}
            onChange={e => onChange({ context: Number(e.target.value) })}
          >
            <option value={0}>0 — {connector?.maleName ?? "Male"}</option>
            <option value={1}>1 — {connector?.femaleName ?? "Female"}</option>
          </select>
          {connector?.type === "Genderless" && (
            <p className="hke__note">Context is ignored for genderless connectors.</p>
          )}
        </div>
      </fieldset>

      {hook.kind === "HookPoint" ? (
        <fieldset className="hke__group">
          <legend>Point behavior</legend>
          <label className="hke__check">
            <input
              type="checkbox"
              checked={hook.required}
              onChange={e => onChange({ required: e.target.checked })}
            />
            Required (load-bearing; drives multi-hook snapping)
          </label>
          <label className="hke__check">
            <input
              type="checkbox"
              checked={hook.swivel}
              onChange={e => onChange({ swivel: e.target.checked })}
            />
            Swivel around the mate
          </label>
          <NumberField
            label="Connection group (0 = ungrouped)"
            value={hook.connectionGroup}
            integer
            onChange={v => onChange({ connectionGroup: Math.round(v) })}
          />
        </fieldset>
      ) : (
        <fieldset className="hke__group">
          <legend>Line geometry</legend>
          <NumberField label="Width (length)" value={hook.width ?? 1.0} onChange={v => onChange({ width: v })} />
          <NumberField
            label="Step (0 = continuous)"
            value={hook.step ?? 0}
            onChange={v => onChange({ step: v })}
          />
          <label className="hke__check">
            <input
              type="checkbox"
              checked={hook.mustBeFullyInsideTarget ?? false}
              onChange={e => onChange({ mustBeFullyInsideTarget: e.target.checked })}
            />
            Must be fully inside target line
          </label>
        </fieldset>
      )}

      <fieldset className="hke__group">
        <legend>Slot</legend>
        <label className="hke__check">
          <input
            type="checkbox"
            checked={hook.slotPickable}
            onChange={e => onChange({ slotPickable: e.target.checked })}
          />
          Pickable slot in the Viewer configurator
        </label>
        <NumberField
          label="Sort order"
          value={hook.sortOrder}
          integer
          onChange={v => onChange({ sortOrder: Math.round(v) })}
        />
      </fieldset>

      <CompatibleProducts
        baseUrl={props.baseUrl}
        connector={connector}
        currentProductId={props.productId}
      />

      <button type="button" className="hke__remove-btn" onClick={props.onRemove}>
        Remove hook
      </button>
    </div>
  );
}

// ─── Compatible products ──────────────────────────────────────────────────

// Session cache: connectorKey → products. Module-level (not per-mount) because
// the inspector remounts per selected hook; compatibility only changes on save,
// so refetching on every selection click would be pure noise. Staleness after
// a save is accepted — a reload shows the updated relational answer.
const compatCache = new Map<string, CompatibleProductDto[]>();

function CompatibleProducts(props: {
  baseUrl: string;
  connector: HookConnectorDto | null;
  currentProductId: string;
}) {
  const { connector } = props;
  const [products, setProducts] = useState<CompatibleProductDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!connector) {
      setProducts(null);
      return;
    }
    const cached = compatCache.get(connector.connectorKey);
    if (cached) {
      setProducts(cached);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    fetchConnectorProducts({
      baseUrl: props.baseUrl,
      connectorKey: connector.connectorKey,
      signal: ctrl.signal,
    })
      .then(list => {
        compatCache.set(connector.connectorKey, list);
        setProducts(list);
      })
      .catch(err => {
        if ((err as Error).name === "AbortError") return;
        setError((err as Error).message ?? String(err));
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [connector, props.baseUrl]);

  return (
    <section className="hke__compat">
      <h3 className="hke__compat-head">Compatible products</h3>
      {!connector ? (
        <p className="hke__muted">Pick a connector to see what can attach here.</p>
      ) : loading ? (
        <p className="hke__muted">Loading…</p>
      ) : error ? (
        <p className="hke__invalid">Failed: {error}</p>
      ) : !products || products.length === 0 ? (
        <p className="hke__muted">
          No other products carry <code>{connector.connectorKey}</code> yet. Compatibility is
          emergent — it appears as soon as another product gets a hook with this connector.
        </p>
      ) : (
        <ul className="hke__compat-list">
          {products.map(p => (
            <li key={p.id} className="hke__compat-row">
              <span className="hke__compat-name">
                {p.name}
                {p.id === props.currentProductId && <span className="hke__muted"> (this product)</span>}
              </span>
              <span className="hke__compat-hint">would attach here</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── Numeric field ────────────────────────────────────────────────────────

/** Controlled numeric input that tolerates in-progress text ("-", "0.")
 *  without fighting the caret: local text state while focused, committed as a
 *  number per keystroke when parseable, resynced from the prop when blurred
 *  or externally changed. */
function NumberField(props: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  integer?: boolean;
}) {
  const [text, setText] = useState(() => fmt(props.value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setText(fmt(props.value));
  }, [props.value, focused]);

  return (
    <label className="hke__num">
      <span className="hke__label">{props.label}</span>
      <input
        type="number"
        className="hke__input hke__input--num"
        step={props.integer ? 1 : 0.01}
        value={text}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          setText(fmt(props.value));
        }}
        onChange={e => {
          setText(e.target.value);
          const n = parseFloat(e.target.value);
          if (Number.isFinite(n)) props.onChange(props.integer ? Math.round(n) : n);
        }}
      />
    </label>
  );
}

export default HookEditorTool;
